import { Article } from '@wix/answers-api';
import { pageClick } from '@wix/bi-logger-new-help-center/v2';
import { Anchors, ConsumerSource } from '@wix/cc-user-facing-common-components';
import { Box, IconButton, Text, Tooltip } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { LayoutThird } from '@wix/wix-ui-icons-common';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { useRef, useState } from 'react';
import { animateScroll } from 'react-scroll';

import { BI } from '../../../constants';
import { DATA_HOOKS } from '../../../dataHooks';
import { useBI } from '../../../hooks/useBI';
import { TableOfContentsIcon } from '../../../icons/TableOfContentsIcon';
import { Step } from '../../../pages/article/[articleTitle]';
import { mapArticleTypeToItemType } from '../../../utils/bi';

import css from './index.module.scss';

type ArticleLeftPanelProps = {
  steps: Step[];
  article: Article;
  copilotArticleContentRef: React.RefObject<HTMLDivElement>;
  anchorsRef: React.RefObject<HTMLDivElement>;
  isCollapsed: boolean;
  onCollapse: () => void;
  onExpand: () => void;
};

export const ArticleLeftPanel = ({
  steps,
  article,
  copilotArticleContentRef,
  anchorsRef,
  isCollapsed,
  onCollapse,
  onExpand,
}: ArticleLeftPanelProps) => {
  const { locale } = useRouter();
  const { t } = useTranslation();
  const { sendBIEvent } = useBI();

  const onAnchorClick = async (text: string, order: string) => {
    animateScroll.scrollToTop();
    await sendBIEvent(
      pageClick({
        source_name: BI.SOURCE_NAMES.ARTICLE,
        kb_lang: locale as string,
        item_id: article.id,
        item_type: mapArticleTypeToItemType(article.type),
        clicked_item_type: BI.CLICKED_ITEM_TYPES.SIDEBAR_ANCHOR,
        clicked_text: text,
        clicked_item_order: order,
        is_page_content_click: true,
      })
    );
  };

  const prevCollapsedRef = useRef(isCollapsed);
  const [shouldAnimate, setShouldAnimate] = useState(false);

  if (prevCollapsedRef.current !== isCollapsed) {
    prevCollapsedRef.current = isCollapsed;
    if (!shouldAnimate) {
      setShouldAnimate(true);
    }
  }

  return (
    <Box
      direction="vertical"
      className={classNames(css.outerWrapper, {
        [css.collapsing]: shouldAnimate && isCollapsed,
        [css.expanding]: shouldAnimate && !isCollapsed,
        [css.collapsed]: !shouldAnimate && isCollapsed,
      })}
    >
      <Box className={classNames(css.collapsedWrapper, { [css.hidden]: !isCollapsed })}>
        <Tooltip content={t('article-page.left-panel.tooltip')} placement="top">
          <IconButton
            skin="dark"
            priority="secondary"
            size="small"
            onClick={onExpand}
          >
            <TableOfContentsIcon />
          </IconButton>
        </Tooltip>
      </Box>
      {!isCollapsed && (
        <Box direction="vertical" className={css.wrapper} ref={anchorsRef}>
          <IconButton
            skin="dark"
            priority="secondary"
            size="small"
            className={css.backButton}
            dataHook={DATA_HOOKS.ARTICLE_LEFT_SIDEBAR_TOGGLE_BUTTON}
            onClick={onCollapse}
          >
            <LayoutThird />
          </IconButton>
          <Text weight="bold" className={css.title}>
            {t('scrollspy.title')}
          </Text>
          <Anchors
            steps={steps}
            onClick={onAnchorClick}
            hideTitle={true}
            locale={locale as string}
            consumerSource={ConsumerSource.HELP_CENTER}
            showScrollToTop={false}
            containerRef={copilotArticleContentRef}
          />
        </Box>
      )}
    </Box>
  );
};
